/**
 * AssetPreview
 *
 * Renders a thumbnail for an asset. Images go through the UI kit's
 * CloudinaryImage; PDFs are rasterized to their first page, falling back to a
 * document icon if Cloudinary can't deliver a rendered page.
 */
import { useState } from 'react';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import { FileTextIcon } from 'lucide-react';

import { getCloudinaryPdfThumbnailUrl } from '@Utils/cloudinary';
import { cn } from '@Utils/cn';
import { AssetType, type Asset } from '@Types/assets';

export interface AssetPreviewProps {
  asset: Asset;
  /** Classes applied to the rendered image. */
  className?: string;
  /** Pixel size requested from Cloudinary for the rasterized PDF page. */
  thumbnailSize?: number;
}

export default function AssetPreview({
  asset,
  className,
  thumbnailSize = 400,
}: AssetPreviewProps) {
  const [rasterFailed, setRasterFailed] = useState(false);

  if (asset.type !== AssetType.DOCUMENT) {
    return (
      <CloudinaryImage
        publicId={asset.cloudinaryId}
        mode="original"
        className={className}
      />
    );
  }

  if (rasterFailed) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-zinc-500">
        <FileTextIcon className="h-10 w-10" />
        <span className="text-xs font-medium">PDF</span>
      </div>
    );
  }

  return (
    <img
      src={getCloudinaryPdfThumbnailUrl(asset.cloudinaryId, {
        width: thumbnailSize,
        height: thumbnailSize,
      })}
      alt=""
      loading="lazy"
      decoding="async"
      className={cn(className)}
      onError={() => setRasterFailed(true)}
    />
  );
}
